import React, { useState, useEffect } from 'react';
import '../../MyProfile/myProfile.scss';
import './ContactUsForm.scss'
import "./ComplaintFormContent.scss";

import ComplaintFormRadioButtons from "./ComplaintFormRadioButtons";
import TextAreaInput from '../../Form/TextInput/TextAreaInput';
import Button, { LoadingButton } from '../../Form/Button/Button';
import Modal from '../../Modal/Modal';
import {emailRegex, emptyString} from '../../../utils/helpers';
import { getEmailValidationRule } from '../../../utils/inputValidations';
import { gql } from 'apollo-boost';
import { useMutation } from '@apollo/react-hooks';
import Loading from '../../Loading/Loading';

import { useCurrentUser } from '../../../contexts/CurrentUserProvider';

import ValidatedInput from '../../Form/TextInput/ValidatedInput';
import withFeatureFlags from '../../FeatureFlags/withFeatureFlags';
import ComplaintFormAlertBox from "./ComplaintFormAlertBox";

const MessageSentModal = (/** @type {{ message: string; onClose: () => void; }} */ { message, onClose }) => {
  return (
      <Modal onClose={onClose}>
        <h4 className="contact-us__third-title">Message sent</h4>
        <p className="modal__paragraph">{message}</p>
        <div className="modal__spacer" />
        <Button size='med' customClassNames={["modal__button", "modal__button--left"]} label="Close" onClick={onClose} />
      </Modal>
  );
};

const VulnerableModal = (/** @type {{ onClose: () => void; }} */ { onClose }) => {
  return (
      <Modal onClose={onClose}>
        <h4 className="modal__success-title modal__success-title--smaller">We're here to help</h4>
        <p className="modal__paragraph">If you have any accessibility needs please call us on 0330 123 3863. We have a dedicated team available to help you from 09:00-17:00 Monday to Friday, excluding bank holidays. </p>
        <div className="modal__spacer" />
      </Modal>
  );
};

/** @import {SendComplaintProps} from './ContactUsFormTypes' */
const SendComplaint = (/** @type {SendComplaintProps} */ { disabled, completeCallback, errorCallback, feedbackType, feedbackText, isLoggedIn, fullName, email, productType, accountNumber }) => {
  const SEND_COMPLAINT = gql`
    mutation SendComplaintLoggedIn($request : SendComplaintLoggedInRequestType!){
      sendComplaintLoggedIn(request:$request) {
        success
      }
    }
  `;

  const SEND_FEEDBACK = gql`
    mutation SendFeedbackLoggedIn($request : SendFeedbackLoggedInRequestType!){
      sendFeedbackLoggedIn(request:$request) {
        success
      }
    }
  `;

  const SEND_COMPLAINT_LOGGED_OUT = gql`
    mutation SendComplaintLoggedOut($request : SendComplaintLoggedOutRequestType!){
      sendComplaintLoggedOut(request:$request) {
        success
      }
    }
  `;

  const SEND_FEEDBACK_LOGGED_OUT = gql`
    mutation SendFeedbackLoggedOut($request : SendFeedbackLoggedOutRequestType!){
      sendFeedbackLoggedOut(request:$request) {
        success
      }
    }
  `;

  const [isLoading, setIsLoading] = useState(false);
  const [sendComplaintMutation] = useMutation(SEND_COMPLAINT);
  const [sendFeedbackMutation] = useMutation(SEND_FEEDBACK);
  const [sendFeedbackLoggedOutMutation] = useMutation(SEND_FEEDBACK_LOGGED_OUT);
  const [sendComplaintLoggedOutMutation] = useMutation(SEND_COMPLAINT_LOGGED_OUT);

  const sendComplaint = async () => {
    const result = await sendComplaintMutation({
      variables: {
        request: {
          complaint: feedbackText,
          productType: productType
        }
      }
    });

    return result.data.sendComplaintLoggedIn.success;
  };
  const sendFeedback = async () => {
    const result = await sendFeedbackMutation({
      variables: {
        request: {
          feedback: feedbackText,
          productType: productType
        }
      }
    });

    return result.data.sendFeedbackLoggedIn.success;
  };
  const sendComplaintLoggedOut = async () => {
    // Extra validation to ensure we don't send empty required fields
    if (!fullName || fullName.trim() === '') {
      throw new Error('Full name is required for logged-out users');
    }
    if (!email || email.trim() === '') {
      throw new Error('Email address is required for logged-out users');
    }

    const result = await sendComplaintLoggedOutMutation({
      variables: {
        request: {
          complaint: feedbackText,
          fullName: fullName.trim(),
          emailAddress: email.trim(),
          productType: productType,
          accountNumber: accountNumber !== undefined && accountNumber !== "" ? accountNumber : null
        }
      }
    });

    return result.data.sendComplaintLoggedOut.success;
  };

  const sendFeedbackLoggedOut = async () => {
    // Extra validation to ensure we don't send empty required fields
    if (!fullName || fullName.trim() === '') {
      throw new Error('Full name is required for logged-out users');
    }
    if (!email || email.trim() === '') {
      throw new Error('Email address is required for logged-out users');
    }

    const result = await sendFeedbackLoggedOutMutation({
      variables: {
        request: {
          feedback: feedbackText,
          fullName: fullName.trim(),
          emailAddress: email.trim(),
          productType: productType,
          accountNumber: accountNumber !== undefined && accountNumber !== "" ? accountNumber : null
        }
      }
    });

    return result.data.sendFeedbackLoggedOut.success;
  };

  const send = async () => {
    setIsLoading(true);

    try {
      let result;
      if (isLoggedIn) {
        result = feedbackType === 'complaint' ?
            await sendComplaint() :
            await sendFeedback();
      }
      else {
        result = feedbackType === 'complaint' ?
            await sendComplaintLoggedOut() :
            await sendFeedbackLoggedOut();
      }

      setIsLoading(false);

      if (result) {
        completeCallback();
      }
      else {
        errorCallback();
      }
    }
    catch {
      setIsLoading(false);
      errorCallback();
    }

  };

  return isLoading ?
      <LoadingButton /> :
      <Button label="Share your experience" disabled={disabled} onClick={send} type="submit" />;
};

const feedbackMessage = () => `Thank you for taking the time to provide your feedback.`;
const complaintMessage = () => `We've received your complaint and will be in touch within 5 working days.`;

/** @import {ContactUsFormProps} from './ContactUsFormTypes' */
function ContactUsForm(/** @type {ContactUsFormProps} */ props) {
  const [feedbackType, setFeedbackType] = useState(/** @type {string | null} */ (null));
  const [text, setText] = useState('');  const [modalOpen, setModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [_valid, setValid] = useState(false);
  const [accountNumber, setAccountNumber] = useState('');
  const [isAccountNumberValid, setIsAccountNumberValid] = useState(true);

  /** @type {(value: string) => boolean} */
  const isValidAccountNumber = value => {
    if (value === undefined || value === null || value.trim() === '') return true;
    return /^\d{8}$/.test(value);
  };

  // Initialize account number validation state
  useEffect(() => {
    setIsAccountNumberValid(isValidAccountNumber(accountNumber));
  }, [accountNumber]);

  const currentUser = useCurrentUser();

  const validate = () => {
    setValid(!emptyString(text) !== null && feedbackType !== null);
  };

  const validateEmail = () => {
    setValid(!emptyString(email) !== null && emailRegex.test(email));
  };

  const setFeedbackTypeWithValidation = (/** @type {string} */ feedbackType) => {
    setFeedbackType(feedbackType);
    validate();
  };

  const setTextWithValidation = (/** @type {string} */ text) => {
    setText(text);
    validate();
  };

  const setEmailWithValidation = (/** @type {string} */ email) => {
    setEmail(email);
    validateEmail();
  };
  const setAccountWithValidation = (/** @type {string} */ account) => {
    setAccountNumber(account);
  };

  const messageSent = () => {
    setModalOpen(true)
  };

  const refreshPage = () => {
    window.location.reload();
  };

  const sendError = () => {
    setErrorMessage(true)
  };
  /** @type {Record<string, { test: (value: string) => boolean; label: string; }>} */
  const accountNumberValidationRule = {
    account_number: {
      test: isValidAccountNumber,
      label: "Account number must be 8 digits"
    }
  };

  const handleAccountNumberValidation = (/** @type {boolean} */ isValid) => {
    setIsAccountNumberValid(isValid);
  };

  const getFeedbackMessage = () => {
    switch (feedbackType) {
      case 'feedback': return feedbackMessage();
      case 'complaint': return complaintMessage();
      default: return '';
    }
  };  const enabledOutside =
      text.length > 0
      && feedbackType !== null
      && name.length > 0
      && email.length > 0
      && emailRegex.test(email)
      && isAccountNumberValid;

  const enabledInside =
      text.length > 0
      && feedbackType !== null;

  if (!currentUser || currentUser.isPending) {
    return <Loading message="We're loading your feedback form..." />
  }
  const isLoggedIn = currentUser.isAuthenticated;

  return (


      <div className="complaintForm_content">

        <h3 className='contact-us__third-title'>Share your complaint or feedback</h3>
        <article className={"complaintForm_rmbhorizontal"}>

          <ComplaintFormAlertBox/>
          <br/>

          <form>
            <div className={"div"}>
              <div style={{marginBottom: "10px"}}>
                <ComplaintFormRadioButtons
                    id="complaint"
                    label="I'd like to make a complaint"
                    value="complaint"
                    checked={feedbackType === "complaint"}
                    onChange={() => setFeedbackTypeWithValidation("complaint")}
                />
              </div>
              <div style={{marginBottom: "10px"}}>
                <ComplaintFormRadioButtons
                    id="feedback"
                    label="I'd like to leave feedback"
                    value="feedback"
                    checked={feedbackType === "feedback"}
                    onChange={() => setFeedbackTypeWithValidation("feedback")}
                />
              </div>
            </div>

            <div className="complaintForm_div2"/>
            <br/>

            <div>
              {!isLoggedIn &&
                  <div>
                    <div className={"complaintForm_div4"}>
                      <label htmlFor="name" className={"complaintForm_yourname"}>
                        Your name
                      </label>
                      <ValidatedInput
                          key="name"
                          capitalise={true}
                          name="name"
                          disabled={props.disabled}
                          maxLength={50}
                          value={name}
                          onChange={v => setName(v)}
                          className={"complaintForm_textfield"}
                      />
                    </div>

                    <div className={"complaintForm_div6"}>
                      <label htmlFor="email" className={"complaintForm_youremailaddress"}>
                        Your email address
                      </label>
                      <ValidatedInput
                          rules={getEmailValidationRule()}
                          onValidation={v => setValid(v)}
                          onChange={setEmailWithValidation}
                          name='email'
                          disabled={props.disabled}
                          inputRef={props.firstInput}
                          value={email}
                          altErrorColor
                          onlyErrors
                          className={"complaintForm_textfield"}
                      />
                    </div>
                  </div>
              }
            </div>

            <div className={"complaintForm_div8"}>
              <label htmlFor="accountNumber" className={"complaintForm_accountnumberoptional"}>
                Account number (optional)
              </label>              <ValidatedInput
                  rules={accountNumberValidationRule}
                  name='account_number'
                  value={accountNumber}
                  onChange={setAccountWithValidation}
                  onValidation={handleAccountNumberValidation}
                  className={"complaintForm_textfield"}
                  maxLength={8}
                  altErrorColor
                  onlyErrors={true}
                  optional={true}
              />
            </div>

            <div className={"complaintForm_div10"}>
              <label htmlFor="feedback" className={"complaintForm_yourcomplaintorfeedback"}>
                Your complaint or feedback
              </label>
              <TextAreaInput
                  name="textArea"
                  onChange={setTextWithValidation}
                  value={text}
              />
            </div>            {isLoggedIn &&
                <SendComplaint
                    disabled={!enabledInside}
                    completeCallback={messageSent}
                    errorCallback={sendError}
                    feedbackType={feedbackType}
                    feedbackText={text}
                    isLoggedIn={isLoggedIn}
                    fullName={name}
                    email={email}
                    productType={feedbackType || 'GENERAL_FEEDBACK'}/>
            }

            {!isLoggedIn &&
                <SendComplaint
                    disabled={!enabledOutside}
                    completeCallback={messageSent}
                    errorCallback={sendError}
                    feedbackType={feedbackType}
                    feedbackText={text}
                    isLoggedIn={isLoggedIn}
                    fullName={name}
                    email={email}
                    productType={feedbackType || 'GENERAL_FEEDBACK'}
                    accountNumber={accountNumber}/>
            }

            {modalOpen && 
              <MessageSentModal message={getFeedbackMessage()} onClose={refreshPage}/>}            <div className={"my-profile__error " + (errorMessage ? "my-profile__error--show" : "")}> 
              <p>There was an error in sending your message. Please check all of the form fields are completed and try
                again.</p>
            </div>
          </form>
        </article>
      </div>
  );
}

export default withFeatureFlags(ContactUsForm);
